import type { Except } from "type-fest";
import { z } from "zod";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "../date-time/iso-and-unix";
import { providerIdZodSchema, type ProviderId } from "../providers/provider-id";
import type { Byte } from "../storage-items/file-size-types";
import {
    storageItemImageIdZodSchema,
    type StorageItemImageId,
} from "../storage-items/storage-item-images/storage-item-image-id";
import { mappableIdZodSchema, type MappableId } from "./mappable-id";
import {
    resourceMapIdFactory,
    resourceMapIdZodSchema,
    type ResourceMapId,
} from "./resource-map-id";

type TextElement = {
    textId: string;
    parentId: MappableId;
};
type ColorSwitchingArea = {
    areaId: string;
    parentId: MappableId;
};

type ResourceMap = {
    _id: ResourceMapId;
    _type: "RESOURCE_MAP";
    _createdAt: IsoAndUnixTimestamp;
    title: string;
    fileSize: Byte;
    storageItemId: StorageItemImageId;
    ownerId: ProviderId;
    mappableIds: MappableId[];
    colorSwitchingAreas: ColorSwitchingArea[];
    textElements: TextElement[];
};

type ResourceMapFactoryParams = Except<
    ResourceMap,
    "_createdAt" | "_id" | "_type"
> & {
    _id?: ResourceMapId;
};
const resourceMapFactory = (params: ResourceMapFactoryParams) => {
    const resourceMap: ResourceMap = {
        ...params,
        _createdAt: nowIsoAndUnix(),
        _id: params._id ?? resourceMapIdFactory(),
        _type: "RESOURCE_MAP",
    };
    return resourceMap;
};

const textElementZodSchema: z.ZodSchema<TextElement> = z.object({
    textId: z.string().min(1),
    parentId: mappableIdZodSchema,
});
const colorSwitchingAreaZodSchema: z.ZodSchema<ColorSwitchingArea> = z.object({
    areaId: z.string().min(1),
    parentId: mappableIdZodSchema,
});
const resourceMapTitleZodSchema = z.string().min(1);
const resourceMapFileSizeZodSchema = z
    .number()
    .min(0) as unknown as z.ZodSchema<Byte>;
const resourceMapFactoryParamsZodSchema: z.ZodSchema<ResourceMapFactoryParams> =
    z.object({
        _id: resourceMapIdZodSchema.optional(),
        title: resourceMapTitleZodSchema,
        fileSize: resourceMapFileSizeZodSchema,
        storageItemId: storageItemImageIdZodSchema,
        ownerId: providerIdZodSchema,
        mappableIds: z.array(mappableIdZodSchema),
        colorSwitchingAreas: z.array(colorSwitchingAreaZodSchema),
        textElements: z.array(textElementZodSchema),
    });

const resourceMapZodSchema: z.ZodSchema<ResourceMap> = z.object({
    _id: resourceMapIdZodSchema,
    _type: z.literal("RESOURCE_MAP"),
    _createdAt: isoAndUnixZodSchema,
    title: resourceMapTitleZodSchema,
    fileSize: resourceMapFileSizeZodSchema,
    storageItemId: storageItemImageIdZodSchema,
    ownerId: providerIdZodSchema,
    mappableIds: z.array(mappableIdZodSchema),
    colorSwitchingAreas: z.array(colorSwitchingAreaZodSchema),
    textElements: z.array(textElementZodSchema),
});

export {
    colorSwitchingAreaZodSchema,
    resourceMapFactory,
    resourceMapFactoryParamsZodSchema,
    resourceMapZodSchema,
    textElementZodSchema,
};
export type {
    ColorSwitchingArea,
    ResourceMap,
    ResourceMapFactoryParams,
    TextElement,
};
